/** Landing copy and sample data. One label per intent across the page:
 * SIGNUP_CTA opens a cabinet, PILOT_CTA asks us to set up a pilot, DEMO_CTA
 * asks the product to call the visitor. */

export const BRAND = 'Anytel'
export const SIGNUP_CTA = 'Спробувати безкоштовно'
export const PILOT_CTA = 'Безкоштовний пілот для бізнесу'
export const DEMO_CTA = 'Отримати дзвінок від AI'
/** Live demo line: answered by the platform's own AI agent. */
export const DEMO_PHONE = '+380738384754'
export const DEMO_PHONE_LABEL = '+380 73 838 47 54'
/** `pattern` for phone inputs. Browsers compile it with the `v` flag, where
 * ( ) and - inside a class must be escaped, or the whole check is dropped. */
export const PHONE_PATTERN = String.raw`[+0-9 \(\)\-]{7,20}`

export interface DialogLine {
  who: 'ai' | 'caller'
  text: string
}

/** What a sample call left behind in the cabinet. */
export interface DialogResult {
  title: string
  rows: [string, string][]
  destination: string
}

export type IndustryKey = 'dental' | 'beauty' | 'auto' | 'food'

export interface Industry {
  key: IndustryKey
  label: string
  /** Sample conversation, caller first. */
  dialog: DialogLine[]
  result: DialogResult
  handles: string[]
}

export const INDUSTRIES: Industry[] = [
  {
    key: 'dental',
    label: 'Стоматологія',
    dialog: [
      { who: 'caller', text: 'Добрий день, скільки коштує професійна чистка? І чи є вільно в суботу?' },
      { who: 'ai', text: 'Чистка коштує 1 400 грн. У суботу вільно об 11:00 у лікарки Марченко. Записати вас?' },
      { who: 'caller', text: 'Так, на одинадцяту. Андрій Коваль.' },
      { who: 'ai', text: 'Записала: субота, 11:00, професійна чистка. Чекаємо вас, Андрію!' },
    ],
    result: {
      title: 'Запис у календарі',
      rows: [
        ['Клієнт', 'Андрій Коваль'],
        ['Послуга', 'Професійна чистка'],
        ['Коли', 'Субота, 11:00'],
        ['Календар', 'Лікарка Марченко'],
      ],
      destination: 'Адміністратор отримав сповіщення в Telegram',
    },
    handles: [
      'Відповідає про ціни, підготовку і графік лікарів',
      'Записує до потрібного лікаря у вільний час',
      'Гострий біль чи складне питання переводить на адміністратора',
    ],
  },
  {
    key: 'beauty',
    label: 'Салон краси',
    dialog: [
      { who: 'caller', text: 'Хочу на манікюр до Олі, завтра після шостої.' },
      { who: 'ai', text: 'Завтра в Олі вільно о 18:30. Манікюр з покриттям гель-лак, правильно?' },
      { who: 'caller', text: 'Так. Мене звати Ірина.' },
      { who: 'ai', text: 'Готово, Ірино: завтра о 18:30, манікюр з покриттям у майстрині Олі.' },
    ],
    result: {
      title: 'Запис у календарі',
      rows: [
        ['Клієнт', 'Ірина'],
        ['Послуга', 'Манікюр, гель-лак'],
        ['Коли', 'Завтра, 18:30'],
        ['Календар', 'Майстриня Оля'],
      ],
      destination: 'Майстриня бачить запис у кабінеті',
    },
    handles: [
      'Знає майстрів, послуги і тривалість кожної',
      'Пропонує найближчий вільний час, а не «передзвоніть пізніше»',
      'Приймає записи ввечері й у вихідні, коли салон зачинено',
    ],
  },
  {
    key: 'auto',
    label: 'Автосервіс',
    dialog: [
      { who: 'caller', text: 'Стукає підвіска. Коли можна приїхати на діагностику?' },
      { who: 'ai', text: 'Найближче вікно в четвер о 9:00. Яка у вас машина і який рік?' },
      { who: 'caller', text: 'Škoda Octavia, 2017. Записуйте, Дмитро.' },
      { who: 'ai', text: 'Записав: четвер, 9:00, діагностика ходової, Škoda Octavia 2017. До зустрічі!' },
    ],
    result: {
      title: 'Нова заявка',
      rows: [
        ['Клієнт', 'Дмитро'],
        ['Авто', 'Škoda Octavia, 2017'],
        ['Послуга', 'Діагностика ходової'],
        ['Коли', 'Четвер, 9:00'],
      ],
      destination: 'Заявка пішла у вашу CRM через вебхук',
    },
    handles: [
      'Збирає марку, рік і симптоми ще до візиту',
      'Записує у вільне вікно на потрібний пост',
      'Передає заявку у вашу CRM або в Telegram майстру',
    ],
  },
  {
    key: 'food',
    label: 'Ресторан',
    dialog: [
      { who: 'caller', text: 'Добрий вечір. Хочу столик на чотирьох на суботу о восьмій.' },
      { who: 'ai', text: 'На суботу о 20:00 є столик на чотирьох біля вікна. На яке ім’я бронювати?' },
      { who: 'caller', text: 'Олег. І чи буде дитячий стілець?' },
      { who: 'ai', text: 'Так, підготуємо. Бронь на Олега: субота, 20:00, четверо гостей.' },
    ],
    result: {
      title: 'Бронювання',
      rows: [
        ['Гість', 'Олег'],
        ['Коли', 'Субота, 20:00'],
        ['Гостей', '4, дитячий стілець'],
        ['Зал', 'Біля вікна'],
      ],
      destination: 'Менеджер зали отримав сповіщення в Telegram',
    },
    handles: [
      'Бронює столики за вільним часом',
      'Відповідає про меню, години роботи і доставку',
      'У пікові години жоден дзвінок не чекає на лінії',
    ],
  },
]

export type DemoCallKey = 'booking' | 'prices' | 'after-hours'

/** The three calls every business gets, as the home page's examples. Sample
 * businesses and data. `audio`: [TODO: аудіо] a real recording of the same
 * conversation, e.g. { src: '/audio/booking.mp3', duration: '0:48' }; the
 * player appears only once it is set. */
export const DEMO_CALLS: {
  key: DemoCallKey
  label: string
  context: string
  dialog: DialogLine[]
  result: DialogResult
  audio?: { src: string; duration: string }
}[] = [
  {
    key: 'booking',
    label: 'Запис на прийом',
    context: 'Клініка сімейної медицини, вівторок, 10:14',
    dialog: [
      { who: 'ai', text: 'Добрий день! Клініка сімейної медицини, я віртуальний адміністратор. Чим можу допомогти?' },
      { who: 'caller', text: 'Хочу записати доньку до педіатра, у неї температура другий день.' },
      { who: 'ai', text: 'До педіатра найближче вільно сьогодні о 16:20 або завтра о 9:00. Як вам зручніше?' },
      { who: 'caller', text: 'Сьогодні, о 16:20. Донька Софія, телефон цей самий.' },
      { who: 'ai', text: 'Записала Софію до педіатра на сьогодні, 16:20. Якщо стане гірше до візиту, телефонуйте 103.' },
    ],
    result: {
      title: 'Запис у календарі',
      rows: [
        ['Пацієнт', 'Софія'],
        ['Лікар', 'Педіатр'],
        ['Коли', 'Сьогодні, 16:20'],
        ['Телефон', '+380 67 ••• 21 09'],
      ],
      destination: 'Запис з’явився в календарі лікаря, адміністратор бачить його в кабінеті',
    },
  },
  {
    key: 'prices',
    label: 'Питання про ціну',
    context: 'Салон краси, субота, 13:52',
    dialog: [
      { who: 'caller', text: 'Скільки у вас коштує фарбування на середню довжину?' },
      { who: 'ai', text: 'Фарбування в один тон на середню довжину від 1 800 грн. Точну ціну майстриня скаже на місці: вона залежить від кількості фарби.' },
      { who: 'caller', text: 'А в неділю працюєте?' },
      { who: 'ai', text: 'Так, у неділю з 10:00 до 18:00. Найближче вільно о 12:00 у майстрині Наталії. Записати вас?' },
      { who: 'caller', text: 'Так, Марина.' },
      { who: 'ai', text: 'Записала, Марино: неділя, 12:00, фарбування в Наталії. Чекаємо вас!' },
    ],
    result: {
      title: 'Запис у календарі',
      rows: [
        ['Клієнтка', 'Марина'],
        ['Послуга', 'Фарбування, середня довжина'],
        ['Коли', 'Неділя, 12:00'],
        ['Майстриня', 'Наталія'],
      ],
      destination: 'Ціну й графік агент узяв з бази знань, яку заповнив салон',
    },
  },
  {
    key: 'after-hours',
    label: 'Дзвінок у неробочий час',
    context: 'Ремонт побутової техніки, середа, 22:40',
    dialog: [
      { who: 'ai', text: 'Добрий вечір! Сервіс ремонту техніки. Зараз ми зачинені, але я прийму заявку, і майстер зателефонує вам зранку.' },
      { who: 'caller', text: 'Пральна машина Bosch не зливає воду. Майстер може приїхати?' },
      { who: 'ai', text: 'Так, майстри виїжджають по місту. Підкажіть адресу і як до вас звертатися.' },
      { who: 'caller', text: 'Вулиця Січових Стрільців, 12, квартира 5. Олександр.' },
      { who: 'ai', text: 'Дякую, Олександре. Передала заявку: Bosch не зливає воду, Січових Стрільців, 12, кв. 5. Майстер зателефонує завтра після 9:00.' },
    ],
    result: {
      title: 'Нова заявка',
      rows: [
        ['Клієнт', 'Олександр'],
        ['Проблема', 'Bosch не зливає воду'],
        ['Адреса', 'Січових Стрільців, 12, кв. 5'],
        ['Зв’язатися', 'Завтра після 9:00'],
      ],
      destination: 'О 22:41 заявка вже була в Telegram майстра',
    },
  },
]

export const STEPS = [
  {
    verb: 'Підключаєте номер',
    text: 'Переадресуйте дзвінки з наявного номера чи АТС або підключіть свого SIP-оператора. Клієнти дзвонять туди ж, куди й раніше.',
  },
  {
    verb: 'Розповідаєте агенту про бізнес',
    text: 'Послуги, ціни, графік, відповіді на часті питання. Звичайним текстом, з готових шаблонів сценаріїв.',
  },
  {
    verb: 'Отримуєте записи і заявки',
    text: 'Ім’я, телефон, послуга і час: у календарі, в Telegram, на пошті або у вашій CRM.',
  },
]

export const FAQ = [
  {
    q: 'Чи доведеться міняти номер телефону?',
    a: 'Ні. Ви налаштовуєте переадресацію з наявного номера чи АТС на адресу Anytel або підключаєте свого SIP-оператора. Клієнти дзвонять туди ж, куди й раніше.',
  },
  {
    q: 'Якою мовою говорить агент?',
    a: 'Українською, англійською та іншими мовами. Голос, манеру і вимову налаштовуєте під свій бренд.',
  },
  {
    q: 'Що, якщо клієнт хоче поговорити з людиною?',
    a: 'Агент переведе дзвінок на співробітника або в чергу операторів, які відповідають прямо з кабінету в браузері. Якщо всі зайняті чи офіс зачинено, агент прийме заявку, а система передзвонить у робочий час.',
  },
  {
    q: 'Агент може записувати клієнтів у наш графік?',
    a: 'Так. У кабінеті є календарі для кожного майстра, лікаря чи кабінету з робочими годинами і вихідними. Агент перевіряє вільний час і записує, подвійних записів система не допускає. Через API календар доступний і вашим системам.',
  },
  {
    q: 'Чи працює агент у месенджерах?',
    a: 'Так. Той самий агент з тими самими знаннями відповідає в Telegram, Viber, Messenger та Instagram, а всі розмови зібрані в одному розділі кабінету.',
  },
  {
    q: 'Де зберігаються записи розмов?',
    a: 'У захищеному сховищі. Дані кожної компанії ізольовані, а співробітники бачать лише ті розділи, до яких ви дали доступ: перегляд або редагування.',
  },
  {
    q: 'Скільки це коштує?',
    a: 'Щомісячна абонплата за кабінет плюс хвилини розмов AI-агента. Точну ціну назвемо після короткої розмови: вона залежить від кількості дзвінків і того, чи потрібен AI на кожному з них.',
  },
  {
    q: 'Чи законно записувати розмови й відповідати голосом AI?',
    a: 'Так, якщо абонента попереджено. На початку кожного дзвінка звучить коротке повідомлення про запис і віртуального асистента. Ми працюємо як розпорядник ваших даних і не використовуємо їх для інших цілей.',
  },
  {
    q: 'Чи можна спершу спробувати?',
    a: 'Так. Зареєструйте компанію, оберіть шаблон сценарію і зробіть тестовий дзвінок прямо з браузера. Пробний період без картки, а перший сценарій можемо налаштувати разом.',
  },
]
